import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  Inject,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FilterObject, ListingsService } from '@out-web/core';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'out-listings-paginator',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './listings-paginator.component.html',
  styleUrl: './listings-paginator.component.scss',
})
export class ListingsPaginatorComponent
  implements OnDestroy, OnChanges, OnInit
{
  @Input() skip = 0;
  @Input() totalResults = 0;
  @Input() loading = false;
  private itemsPerPage = 9;
  @Output() pageChange = new EventEmitter<FilterObject>();
  filters: FilterObject = {};

  private _skip$ = new BehaviorSubject<number>(0);
  private _totalResults$ = new BehaviorSubject<number>(0);

  total$ = combineLatest([this._skip$, this._totalResults$]).pipe(
    map(([skip, totalResults]) => {
      if (skip === null || skip === undefined) {
        return 0;
      }
      return Math.ceil(totalResults / this.itemsPerPage);
    })
  );

  page$ = combineLatest([this._skip$]).pipe(
    map((skip) => {
      const currentSkip = skip[0];
      if (currentSkip === null || currentSkip === undefined) {
        return 1;
      }
      if (typeof currentSkip !== 'number') {
        throw new Error('skip must be a number');
      }
      return Math.floor(currentSkip / this.itemsPerPage) + 1;
    })
  );

  private subscription: Subscription;

  constructor(
    private listingService: ListingsService,
    private route: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.subscription = this.listingService.listingsState$.subscribe(
      (state) => {
        this._skip$.next(state.skipListings || 0);
        this._totalResults$.next(state.totalListings || 0);
        this.loading = state.listingPending;
      }
    );
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.filters = { ...params };
    });
  }

  ngOnChanges() {
    this._totalResults$.next(this.totalResults);
    this._skip$.next(this.skip);
  }

  older() {
    this.filters = {
      ...this.route.snapshot.queryParams,
      Skip: this._skip$.value + 9,
    };
    this.listingService.setSkipListings(Number(this.filters.Skip));
    this.pageChange.emit({ ...this.filters });
  }

  newer() {
    this.filters = {
      ...this.route.snapshot.queryParams,
      Skip: this._skip$.value - 9,
    };
    this.listingService.setSkipListings(Number(this.filters.Skip));
    this.pageChange.emit({ ...this.filters });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  goToPage(page: number) {
    if (
      page < 1 ||
      page > Math.ceil(this._totalResults$.value / this.itemsPerPage)
    )
      return;

    this.filters = {
      ...this.route.snapshot.queryParams,
      Skip: (page - 1) * this.itemsPerPage,
    };

    this.listingService.setSkipListings(Number(this.filters.Skip));
    this.pageChange.emit({ ...this.filters });
  }
}
